import React, { useMemo, useRef, useState, useEffect, memo } from 'react';
import { useSelector } from 'react-redux';
import { Spinner, Icon } from '@iq/react-components';
import classnames from 'classnames';
import { getActiveSite } from '../../../../../bundles/sites';
import { useSubscription, useRefetchOnRangeChange } from '../../../../../bundles/visualizations';
import { getTimezone } from '../../../../../bundles/application';
import { getSiteVariablesIndex } from '../../../../../bundles/sources';
import { useClientSize } from '../../../../../utils';
import { resolveVariableUnit } from '../../../../../units';
import { resolveThresholdItems } from '../../schemas/thresholdSchema';
import { DropdownMenu } from '../../../../DropdownMenu';

import Loader from '../../../../Loader';

import {
  triangulateVectors,
  getPlotCoordinates,
  tooltipLabelFormatter,
  getRequiredStarSignalCount,
} from './helpers';
import {
  AreaThreshold,
  LinearThreshold,
  RadialGrid,
  StarAxes,
  StarLegend,
  StarPlot,
  RollingAveragePlot,
  StarSignalCountMessage,
} from './components';
import { useStarScale } from './hooks/useStarScale';

/**
 * Star Visualization: plots 3 signals as a single point using vector addition
 * on 3 axes spaced 120° apart (blue = L1 / up, red = L2 / lower-left,
 * green = L3 / lower-right). The point's distance from the center is the
 * combined magnitude, and its direction shows which signal(s) dominate.
 *
 * Supports plotting the latest reading ("current") or a trail ("over time"),
 * with optional rolling average, radial grid and circular thresholds
 * (linear rings or area bands) centered on a configurable origin.
 */

const StarVisualization = memo(
  ({
    visualization,
    panelId,
    pollingDateRange,
    onExportData,
    onFullscreen,
    isPreview,
    isFullscreen,
    selected,
    onSelect,
    openSignalViewer,
    CustomTooltip,
    getMoreOptions,
    dropdownPlacement,
    visualizationName,
    hideToolbar,
  }) => {
    const site = useSelector(getActiveSite);
    const timezone = useSelector(getTimezone);
    const variablesIndex = useSelector(getSiteVariablesIndex);
    const [legendHeight, setLegendHeight] = useState(0);

    const [tooltip, setTooltip] = useState(null);

    const {
      initialLoaded,
      seriesValues: values,
      loading,
    } = useSubscription(visualization.id, panelId);
    const { variables = [], configuration = {} } = visualization;
    const inputMode = configuration.inputMode || 'vectors';
    const isCartesian = inputMode === 'cartesian';
    const rollingAverageEnabled = !isCartesian && configuration.rollingAverage?.show;
    const [{ height, width }, clientReference] = useClientSize();
    const legendReference = useRef(null);

    const sortedThresholds = useMemo(
      () => resolveThresholdItems(configuration.thresholds || {}, variables),
      [configuration.thresholds, variables]
    );

    useEffect(() => {
      if (legendReference.current) {
        setLegendHeight(legendReference.current.clientHeight);
      }
    }, []);

    useRefetchOnRangeChange({
      visualization,
      panelId,
      initialLoaded,
      selected,
      rangeKey: `${pollingDateRange.startDate}:${pollingDateRange.endDate}`,
    });

    const data = useMemo(() => {
      if (!values) return [];
      const hasConfig = variables.some((variable) =>
        values.some((value) => value.variable === variable.id)
      );
      if (!hasConfig) return [];
      return values.reduce((acc, { values: dataValues, name, granularity }) => {
        for (const [time, value, quality] of dataValues) {
          const parsedValue = Number.parseFloat(value);
          const cleanedValue = Number.isNaN(parsedValue) ? undefined : parsedValue;

          let axisAlignedValue = cleanedValue;

          if (quality !== 1) axisAlignedValue = null;

          const index = acc.findIndex((entry) => entry.time === time);
          if (index === -1) {
            const insertIndex = acc.findIndex(
              (_, index_) => acc[index_].time > time && (acc[index_ - 1] || {}).time < time
            );
            if (insertIndex === -1) {
              acc.push({
                time,
                [name]: axisAlignedValue,
                granularity,
              });
            } else {
              acc = [
                ...acc.slice(0, insertIndex),
                {
                  time,
                  [name]: axisAlignedValue,
                  granularity,
                },
                ...acc.slice(insertIndex),
              ];
            }
          } else {
            acc[index] = {
              ...acc[index],
              [name]: axisAlignedValue,
            };
          }
        }
        return acc;
      }, []);
    }, [values, variables, variablesIndex, isPreview]);

    const series = useMemo(
      () =>
        variables.map(({ id, aggregate, label, unit, unitLabel, decimals }) => {
          const { name } = (variablesIndex || {})[id] || {};
          const seriesId = `${id}.${aggregate}`;
          const { displayUnit, customUnit } = resolveVariableUnit(
            unit,
            unitLabel,
            (variablesIndex || {})[id]?.unit
          );

          return {
            variableId: id,
            id: seriesId,
            label: label || name,
            decimals: decimals || 'auto',
            unit: displayUnit,
            customUnit,
            aggregate,
          };
        }),
      [variables, variablesIndex, configuration]
    );
    const filteredData = useMemo(() => {
      // Use auto-assigned defaults if plot is not configured
      const plot = configuration.plot || {};
      const defaultPlot = isCartesian
        ? {
            X: series[0]?.id,
            Y: series[1]?.id,
          }
        : {
            L1: series[0]?.id,
            L2: series[1]?.id,
            L3: series[2]?.id,
          };
      const finalPlot = {
        ...(isCartesian ? defaultPlot : defaultPlot),
        ...plot,
      };

      if (isCartesian) {
        return data
          .filter((d) => d[finalPlot.X] != null && d[finalPlot.Y] != null)
          .map((point) => ({
            ...point,
            magnitude: Math.hypot(point[finalPlot.X], point[finalPlot.Y]).toFixed(2),
          }));
      }

      function getMagnitude(filteredPoint) {
        const [X, Y] = triangulateVectors({
          M1: filteredPoint[finalPlot.L1],
          M2: filteredPoint[finalPlot.L2],
          M3: filteredPoint[finalPlot.L3],
        });
        return Math.hypot(X, Y).toFixed(2);
      }

      return data
        .filter(
          (d) => d[finalPlot.L1] != null && d[finalPlot.L2] != null && d[finalPlot.L3] != null
        )
        .map((filteredPoint) => ({
          ...filteredPoint,
          magnitude: getMagnitude(filteredPoint),
        }));
    }, [data, inputMode, configuration.plot, series]);

    const rollingData = useMemo(() => {
      if (!rollingAverageEnabled) return [];

      let Xrolling = 0;
      let Yrolling = 0;
      const minSeries = 3;

      return (
        series.length >= minSeries &&
        filteredData?.map((value, index) => {
          const [X, Y] = getPlotCoordinates({
            dataPoint: value,
            configuration,
            series,
            inputMode,
          });

          Xrolling =
            index === 0
              ? X
              : configuration.rollingAverage.decay * Xrolling +
                (1 - configuration.rollingAverage.decay) * X;

          Yrolling =
            index === 0
              ? Y
              : configuration.rollingAverage.decay * Yrolling +
                (1 - configuration.rollingAverage.decay) * Y;
          return { X: Xrolling, Y: Yrolling };
        })
      );
    }, [configuration, filteredData, rollingAverageEnabled, series, inputMode]);

    function PointTooltip() {
      return (
        <div
          className="tooltip"
          style={{ left: tooltip.pos.x, top: tooltip.pos.y }}
        >
          <div className="tooltip-time">
            {tooltip.data.time === 0 ? 'Error' : tooltipLabelFormatter(tooltip.data.time, timezone)}
          </div>
          <div style={{ color: tooltip.data.color }}>
            <div className="tooltip-item">{configuration.plot?.label}</div>
            {isCartesian ? (
              <>
                <div className="tooltip-item">
                  {series.find((s) => s.id === configuration.plot?.X)?.label || series[0]?.label} :{' '}
                  {tooltip.data.M1}
                </div>
                <div className="tooltip-item">
                  {series.find((s) => s.id === configuration.plot?.Y)?.label || series[1]?.label} :{' '}
                  {tooltip.data.M2}
                </div>
              </>
            ) : (
              <>
                <div className="tooltip-item">
                  {series.find((s) => s.id === configuration.plot?.L1)?.label || series[0]?.label} :{' '}
                  {tooltip.data.M1}
                </div>
                <div className="tooltip-item">
                  {series.find((s) => s.id === configuration.plot?.L2)?.label || series[1]?.label} :{' '}
                  {tooltip.data.M2}
                </div>
                <div className="tooltip-item">
                  {series.find((s) => s.id === configuration.plot?.L3)?.label || series[2]?.label} :{' '}
                  {tooltip.data.M3}
                </div>
              </>
            )}
            <div className="tooltip-item">Magnitude : {tooltip.data.magnitude}</div>
          </div>
        </div>
      );
    }

    const handleDownloadClick = () => {
      onExportData(data[0].granularity);
    };
    const handleSelect = () => {
      onSelect(visualization);
    };
    const classes = classnames('menu', {});

    const isVizEmpty = !filteredData?.length && !loading && initialLoaded;
    const showOptionsMenu = !isPreview && !isVizEmpty;

    const vizHeight =
      height - (isPreview ? 16 : 28) - (configuration.legend?.show ? legendHeight : 0);
    // less loader padding
    const loaderHeight = vizHeight - 28;
    const vizSize = Math.min(vizHeight, width);

    const [scale, largest] = useStarScale({
      filteredData,
      configuration,
      series,
      vizSize,
      data,
      inputMode,
    });

    const requiredSignalCount = getRequiredStarSignalCount(inputMode);
    if (series.length < requiredSignalCount) {
      return (
        <div className="star-error-container">
          <Icon
            icon="he-info"
            size="s"
          />
          <span className="error-message">
            <StarSignalCountMessage
              visualizationName={visualizationName}
              boldClassName="error-bold"
            />
          </span>
        </div>
      );
    }

    return (
      <div
        className={`star-visualization-component ${selected ? 'checked' : ''}`}
        ref={clientReference}
      >
        {!isPreview && !isFullscreen ? (
          <div
            className="star-header"
            onDoubleClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="header-title truncateText">
              {!hideToolbar && (
                <div
                  className={`select ${selected ? 'checked' : ''}`}
                  onClick={handleSelect}
                >
                  <Icon
                    icon={`${selected ? 'he-checkbox-selected' : 'he-checkbox'}`}
                    size="s"
                  />
                </div>
              )}
              <CustomTooltip content={configuration?.title || visualization?.name}>
                <span className="title truncateText">
                  {configuration?.title || visualization?.name || '\u00A0'}
                </span>
              </CustomTooltip>
            </div>
            <div className={classes}>
              {showOptionsMenu && (
                <DropdownMenu
                  trigger="he-moreoptionsvertical"
                  triggerSize="s"
                  menuOptions={getMoreOptions({
                    loading,
                    site,
                    isPreview,
                    onFullscreen,
                    data,
                    handleDownloadClick,
                    openSignalViewer,
                  })}
                  menuXPlacement={dropdownPlacement?.x}
                  menuYPlacement={dropdownPlacement?.y}
                  styles={{
                    dropdownIndicator: { padding: '0px' },
                    option: { cursor: 'pointer' },
                  }}
                />
              )}
            </div>
            {initialLoaded && loading && (
              <Spinner
                size="s"
                className="spinner"
              />
            )}
          </div>
        ) : (
          !isFullscreen && (
            <div className="star-preview-title">
              <span className="title">{configuration.title || visualization.name || '\u00A0'}</span>
            </div>
          )
        )}

        {!initialLoaded && loading ? (
          <Loader
            text="Loading ..."
            height={loaderHeight}
          />
        ) : (
          <div className="star-wrapper">
            {tooltip && <PointTooltip />}
            <div className="star-container">
              <svg
                height={Math.max(vizSize, 0)}
                width={Math.max(vizSize, 0)}
                onMouseLeave={() => {
                  setTooltip(null);
                }}
              >
                <rect
                  className="star-border"
                  width={Math.max(vizSize, 0)}
                  height={Math.max(vizSize, 0)}
                  fill="transparent"
                  strokeWidth={2}
                />
                {configuration.thresholds?.thresholdType === 'linear' &&
                  sortedThresholds.map((threshold, index) => (
                    <LinearThreshold
                      threshold={threshold}
                      filteredData={filteredData}
                      configuration={configuration}
                      series={series}
                      vizSize={vizSize}
                      scale={scale}
                      inputMode={inputMode}
                      key={index}
                    />
                  ))}
                {configuration.thresholds?.thresholdType === 'area' &&
                  sortedThresholds.map((threshold, index) => (
                    <AreaThreshold
                      threshold={threshold}
                      filteredData={filteredData}
                      configuration={configuration}
                      series={series}
                      vizSize={vizSize}
                      scale={scale}
                      inputMode={inputMode}
                      key={index}
                    />
                  ))}
                <RadialGrid
                  configuration={configuration}
                  vizSize={vizSize}
                  scale={scale}
                  largest={largest}
                />
                <g
                  name="axes"
                  strokeWidth={1}
                  strokeDasharray="4 4"
                >
                  <StarAxes
                    vizSize={vizSize}
                    inputMode={inputMode}
                  />
                </g>
                <StarPlot
                  filteredData={filteredData}
                  configuration={configuration}
                  series={series}
                  vizSize={vizSize}
                  scale={scale}
                  onTooltipChange={setTooltip}
                  inputMode={inputMode}
                />
                {rollingAverageEnabled && (
                  <RollingAveragePlot
                    rollingData={rollingData}
                    configuration={configuration}
                    vizSize={vizSize}
                    scale={scale}
                  />
                )}
              </svg>
              {!!configuration?.legend?.show && (
                <StarLegend
                  configuration={configuration}
                  series={series}
                  legendReference={legendReference}
                  inputMode={inputMode}
                  sortedThresholds={sortedThresholds}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
  (previousProperties, properties) =>
    JSON.stringify(previousProperties) === JSON.stringify(properties)
);

export default StarVisualization;
